/**
 * WhatsNewBanner — Dismissible in-app announcement for newly-shipped features.
 *
 * Surfaces ONE active announcement at a time on the Dashboard.  Per-user
 * dismissal is persisted in localStorage so each user can dismiss
 * independently. When a new feature ships, bump the `key` constant and the
 * banner re-appears for everyone.
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Sparkles, ArrowRight, BadgeDollarSign } from "lucide-react";

// To re-announce, bump the key (e.g. "v2") and the banner shows again for everyone.
const ANNOUNCEMENT = {
  key: "affiliate-program-v1",
  eyebrow: "New",
  title: "We just launched the Affiliate Program",
  body: "Earn 30% recurring for 12 months on every paying customer you refer. Payouts hit your bank — or your own subscription as credit.",
  cta: "Activate my affiliate link",
  to: "/v3/growth/affiliate",
  icon: BadgeDollarSign,
};

export default function WhatsNewBanner() {
  const storageKey = `whatsnew-dismissed-${ANNOUNCEMENT.key}`;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setVisible(!localStorage.getItem(storageKey));
  }, [storageKey]);

  const dismiss = () => {
    try {
      localStorage.setItem(storageKey, new Date().toISOString());
    } catch {
      /* private-browsing — banner won't persist but UX still works */
    }
    setVisible(false);
  };

  if (!visible) return null;

  const Icon = ANNOUNCEMENT.icon || Sparkles;
  return (
    <div
      className="relative mb-6 overflow-hidden rounded-2xl border border-emerald-200"
      style={{
        background:
          "linear-gradient(115deg, #ECFDF5 0%, #D1FAE5 35%, #FEF3C7 100%)",
      }}
      data-testid="whats-new-banner"
    >
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
        background: "radial-gradient(50% 100% at 90% 50%, rgba(16,185,129,0.35) 0%, transparent 70%)",
      }} />
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-4 p-5 sm:p-6">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className="shrink-0 w-12 h-12 rounded-xl bg-white shadow-sm border border-emerald-200 flex items-center justify-center text-emerald-700">
            <Icon size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-emerald-800 mb-1">
              <Sparkles size={10} /> {ANNOUNCEMENT.eyebrow}
            </div>
            <div className="font-display font-extrabold text-lg sm:text-xl tracking-tight leading-snug">
              {ANNOUNCEMENT.title}
            </div>
            <p className="text-sm text-[var(--ink-2)] mt-1 leading-relaxed max-w-2xl">
              {ANNOUNCEMENT.body}
            </p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="shrink-0 w-8 h-8 rounded-full hover:bg-white/60 flex items-center justify-center text-[var(--ink-2)] hover:text-[var(--ink)] transition-colors sm:hidden"
            aria-label="Dismiss announcement"
            data-testid="whats-new-dismiss-mobile"
          >
            <X size={16} />
          </button>
        </div>
        <Link
          to={ANNOUNCEMENT.to}
          className="inline-flex items-center justify-center gap-1.5 bg-[var(--ink)] text-white font-bold text-sm px-4 py-2.5 rounded-lg hover:opacity-90 transition-opacity w-full sm:w-auto sm:shrink-0"
          data-testid="whats-new-cta"
        >
          {ANNOUNCEMENT.cta} <ArrowRight size={14} />
        </Link>
        <button
          type="button"
          onClick={dismiss}
          className="hidden sm:flex shrink-0 w-8 h-8 rounded-full hover:bg-white/60 items-center justify-center text-[var(--ink-2)] hover:text-[var(--ink)] transition-colors"
          aria-label="Dismiss announcement"
          data-testid="whats-new-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
